/**
 * client/emotes.ts
 * Responsabilidad: Gestor de estado del cliente, precarga de SVGs Twemoji en memoria,
 * llamadas REST y cooldown local de 3s para el sistema de emotes sociales.
 */

import { safeGetStorage } from "./utils";
import { questManager } from "./quests";
import {
    EMOTE_CATALOG,
    EMOTES_CATALOG,
    EMOTE_MAX_EQUIPPED,
    EMOTE_COOLDOWN_MS,
    EMOTE_DURATION_MS,
    type EmoteDefinition,
    type EmoteCatalogItem,
    type EmoteCurrency,
    type EmoteTier,
} from "../src/shared/constants";

export {
    EMOTE_CATALOG,
    EMOTES_CATALOG,
    EMOTE_MAX_EQUIPPED,
    EMOTE_COOLDOWN_MS,
    EMOTE_DURATION_MS,
    type EmoteDefinition,
    type EmoteCatalogItem,
    type EmoteCurrency,
    type EmoteTier,
};

export interface EmoteStatusResponse {
    unlockedEmotes: string[];
    equippedEmotes: string[];
    crystals: number;
    astralOrbs: number;
}

export interface EmoteBuyResponse {
    success: boolean;
    error?: string;
    message?: string;
    unlockedEmotes?: string[];
    equippedEmotes?: string[];
    crystals?: number;
    astralOrbs?: number;
}

export interface EmoteEquipResponse {
    success: boolean;
    equippedEmotes?: string[];
    error?: string;
    message?: string;
}

export class EmoteManager {
    private dynamicCatalog: EmoteDefinition[] | null = null;
    private unlockedEmotes: string[] = [];
    private equippedEmotes: string[] = [];
    private crystals: number = 0;
    private astralOrbs: number = 0;
    private lastEmoteSentAt: number = 0;

    // Cache en memoria de imágenes SVG precargadas para renderizado Canvas sin parpadeos a 60 FPS
    private imageCache = new Map<string, HTMLImageElement>();
    private imageLoadedMap = new Map<string, boolean>();

    private listeners: Array<() => void> = [];

    constructor() {
        this.preloadAllEmoteImages();

        if (typeof window !== "undefined") {
            void this.fetchCatalog();
            void this.refreshStatus();
            window.addEventListener("alchem:auth_change", () => {
                void this.refreshStatus();
            });
        }
    }

    /**
     * Obtiene la lista ordenada de emotes (dinámica de la BD o fallback a EMOTE_CATALOG).
     */
    public getCatalogList(): EmoteDefinition[] {
        return this.dynamicCatalog || Object.values(EMOTE_CATALOG);
    }

    /**
     * Sincroniza el catálogo autoritativo de emotes desde /api/shop/emotes.
     */
    public async fetchCatalog(): Promise<void> {
        try {
            const res = await fetch("/api/shop/emotes");
            if (res.ok) {
                const data = await res.json();
                if (data && data.success && Array.isArray(data.catalog)) {
                    this.dynamicCatalog = data.catalog;
                    this.preloadAllEmoteImages();
                    this.notify();
                }
            }
        } catch (e) {
            console.warn("[Emotes] Error al consultar /api/shop/emotes:", e);
        }
    }

    /**
     * Precarga síncrona en memoria de los SVGs de Twemoji.
     */
    private preloadAllEmoteImages(): void {
        if (typeof Image === "undefined") return;

        for (const def of this.getCatalogList()) {
            const id = def.id;
            if (this.imageCache.has(id)) continue;
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
                this.imageLoadedMap.set(id, true);
            };
            img.onerror = () => {
                this.imageLoadedMap.set(id, false);
            };
            img.src = def.svgUrl;
            this.imageCache.set(id, img);
        }
    }

    /**
     * Obtiene el objeto HTMLImageElement precargado para el canvas.
     */
    public getEmoteImage(emoteId: string): { image: HTMLImageElement | null; isLoaded: boolean } {
        const img = this.imageCache.get(emoteId);
        const isLoaded = !!img && this.imageLoadedMap.get(emoteId) === true && img.complete && img.naturalWidth > 0;
        return { image: img || null, isLoaded };
    }

    public getUnlockedEmotes(): string[] {
        return [...this.unlockedEmotes];
    }

    public getEquippedEmotes(): string[] {
        return [...this.equippedEmotes];
    }

    public isUnlocked(emoteId: string): boolean {
        return this.unlockedEmotes.includes(emoteId);
    }

    public isEquipped(emoteId: string): boolean {
        return this.equippedEmotes.includes(emoteId);
    }

    public getCrystals(): number {
        return this.crystals;
    }

    public getAstralOrbs(): number {
        return this.astralOrbs;
    }

    /**
     * Cooldown local de 3s (anti-spam visual).
     */
    public canSendEmote(): boolean {
        return Date.now() - this.lastEmoteSentAt >= EMOTE_COOLDOWN_MS;
    }

    public getCooldownRemainingMs(): number {
        const elapsed = Date.now() - this.lastEmoteSentAt;
        return Math.max(0, EMOTE_COOLDOWN_MS - elapsed);
    }

    public recordEmoteSent(): void {
        this.lastEmoteSentAt = Date.now();
        this.notify();
    }

    public subscribe(listener: () => void): () => void {
        this.listeners.push(listener);
        listener();
        return () => {
            this.listeners = this.listeners.filter((l) => l !== listener);
        };
    }

    private notify(): void {
        for (const listener of this.listeners) {
            listener();
        }
    }

    private getAuthToken(): string | null {
        return (
            safeGetStorage("alchem_auth_token") ||
            safeGetStorage("colyseus-auth-token") ||
            null
        );
    }

    /**
     * Sincroniza el catálogo desbloqueado y rueda equipada con el backend.
     */
    public async refreshStatus(): Promise<EmoteStatusResponse | null> {
        const token = this.getAuthToken();
        if (!token) {
            this.unlockedEmotes = [];
            this.equippedEmotes = [];
            this.notify();
            return null;
        }

        try {
            const res = await fetch("/api/emotes/status", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!res.ok) {
                if (res.status === 401) {
                    this.unlockedEmotes = [];
                    this.equippedEmotes = [];
                    this.notify();
                }
                // Si la respuesta no es 200 (ej. 401, 500), no bloqueamos ni sobrescribimos
                // el catálogo estático EMOTES_CATALOG / EMOTE_CATALOG
                return null;
            }

            const data = (await res.json()) as EmoteStatusResponse;
            this.unlockedEmotes = Array.isArray(data.unlockedEmotes) ? data.unlockedEmotes : [];
            this.equippedEmotes = Array.isArray(data.equippedEmotes)
                ? data.equippedEmotes.slice(0, EMOTE_MAX_EQUIPPED)
                : [];
            this.crystals = typeof data.crystals === "number" ? data.crystals : 0;
            this.astralOrbs = typeof data.astralOrbs === "number" ? data.astralOrbs : 0;

            questManager.updateBalances(this.crystals, this.astralOrbs);
            this.notify();
            return data;
        } catch (e) {
            console.warn("[Emotes] Error al consultar /api/emotes/status:", e);
            return null;
        }
    }

    /**
     * Alias de refreshStatus() para compatibilidad de interfaz.
     */
    public async fetchStatus(): Promise<EmoteStatusResponse | null> {
        return this.refreshStatus();
    }

    /**
     * Compra un emote de forma autoritativa.
     */
    public async buyEmote(emoteId: string): Promise<EmoteBuyResponse> {
        const token = this.getAuthToken();
        if (!token) {
            return {
                success: false,
                error: "not_logged_in",
                message: "Debes iniciar sesión con tu cuenta para comprar y guardar emotes.",
            };
        }

        try {
            const res = await fetch("/api/emotes/buy", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ emoteId }),
            });

            const data = (await res.json()) as EmoteBuyResponse;
            if (data.success) {
                if (Array.isArray(data.unlockedEmotes)) {
                    this.unlockedEmotes = data.unlockedEmotes;
                }
                if (Array.isArray(data.equippedEmotes)) {
                    this.equippedEmotes = data.equippedEmotes.slice(0, EMOTE_MAX_EQUIPPED);
                }
                if (typeof data.crystals === "number") {
                    this.crystals = data.crystals;
                }
                if (typeof data.astralOrbs === "number") {
                    this.astralOrbs = data.astralOrbs;
                }

                questManager.updateBalances(this.crystals, this.astralOrbs);
                this.notify();
            }
            return data;
        } catch (e) {
            console.error("[Emotes] Error en /api/emotes/buy:", e);
            return {
                success: false,
                error: "network_error",
                message: "Error de red al procesar la compra del emote.",
            };
        }
    }

    /**
     * Guarda la selección de hasta 5 emotes equipados en la rueda radial.
     */
    public async equipEmotes(equippedEmotes: string[]): Promise<EmoteEquipResponse> {
        const token = this.getAuthToken();
        if (!token) {
            return {
                success: false,
                error: "not_logged_in",
                message: "Debes iniciar sesión para equipar emotes.",
            };
        }

        try {
            const res = await fetch("/api/emotes/equip", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ equippedEmotes }),
            });

            const data = (await res.json()) as EmoteEquipResponse;
            if (data.success && Array.isArray(data.equippedEmotes)) {
                this.equippedEmotes = data.equippedEmotes.slice(0, EMOTE_MAX_EQUIPPED);
                this.notify();
            }
            return data;
        } catch (e) {
            console.error("[Emotes] Error en /api/emotes/equip:", e);
            return {
                success: false,
                error: "network_error",
                message: "Error de red al sincronizar emotes equipados.",
            };
        }
    }
}

export const emoteManager = new EmoteManager();
